/**
 * 资料采集清单的纯函数(屏 2 右栏 / 屏 3 右栏共用):状态 → 色调、来源标签兜底、分组进度、值的人话、
 * 图例计数、「生成初稿」的闸、要不要自动预填。没有 React、没有请求,可单测。
 */
import type {
  Checklist,
  ChecklistConflict,
  ChecklistGroup,
  ChecklistItem,
  ChecklistStatus,
  CollectAnswer,
  FieldSpec,
  FieldType,
  Question,
  SiteProgress,
} from "../api/types";
import { optionLabel } from "./schemaLabels";

export type StatusTone = "ok" | "warn" | "missing" | "default" | "excluded" | "creative";

/**
 * prefilled / confirmed → ok;conflict → warn(待你确认);missing → missing;
 * skipped(带默认值、不预填不追问,附录 A.25)→ default;excluded(案例未授权公开,附录 A.21)→ excluded;
 * creative(模板 `source: ai` 的创意字段,附录 A.33)→ creative(中性灰,跟 default 一样不催人)。
 */
export function statusTone(status: ChecklistStatus): StatusTone {
  switch (status) {
    case "prefilled":
    case "confirmed":
      return "ok";
    case "conflict":
      return "warn";
    case "missing":
      return "missing";
    case "skipped":
      return "default";
    case "excluded":
      return "excluded";
    case "creative":
      return "creative";
  }
}

export const STATUS_LABEL: Record<ChecklistStatus, string> = {
  prefilled: "已预填",
  confirmed: "已确认",
  conflict: "待确认",
  missing: "缺失",
  skipped: "默认",
  excluded: "不公开",
  creative: "AI 起草",
};

/** creative 行没有值时右侧那句(附录 A.33:创意字段不采集,出初稿时由 AI 写)。 */
export const CREATIVE_PENDING_TEXT = "生成初稿时由 AI 起草";

/** news_feed 行右侧那句(附录 A.33:新闻舱自动同步,不采集)。 */
export const NEWS_SYNC_TEXT = "自动同步";

/**
 * 不进任何计数的状态(附录 A.33 / A.21):creative(AI 起草)、skipped(带默认值 / news_feed)、
 * excluded(不公开)—— 照常列出,但不进分组进度、不进图例、不触发缺失。
 */
const NOT_COUNTED_STATUSES: ReadonlySet<ChecklistStatus> = new Set([
  "creative",
  "skipped",
  "excluded",
]);

/**
 * 这一行算不算「可采集」(进分组进度 / 图例 / 生成初稿的闸):状态不在 {@link NOT_COUNTED_STATUSES},
 * 且字段类型不是 `news_feed`。★ 类型直接读行上的 `type`:api 清单每行都把模板字段的 type 抄上
 * (`CollectChecklistAssembler.item` → `f.type()`),不需要另拉模板;行上没带 type 时退回只按状态判。
 */
export function isCollectableRow(item: { status: ChecklistStatus; type?: FieldType }): boolean {
  return !NOT_COUNTED_STATUSES.has(item.status) && item.type !== "news_feed";
}

/**
 * 后端已给人话 `sourceLabel`(如「公司简介.pdf · P2」)就原样用;没给时只按 sourceRef 前缀兜底
 * (附录 A.20:`user:` 来源为「你填写的」,`ai:` 为「AI 起草」),`kb:` 的人话必须由后端给(docName
 * 我们这边没有),兜底成「—」,不自己拼。
 */
export function sourceLabel(
  item: Pick<ChecklistItem, "sourceLabel"> & { source?: string | null }
): string {
  const given = item.sourceLabel?.trim();
  if (given) return given;
  const ref = item.source ?? "";
  if (ref.startsWith("user:")) return "你填写的";
  if (ref.startsWith("ai:")) return "AI 起草";
  return "—";
}

/**
 * 清单一行的显示名。api 每行只给模板 label(三款产品都叫「产品名」,`CollectChecklistAssembler.item` 抄的是
 * `f.label()`),repeat 实例行按路径里**最后一个**下标补「 · N」(1-based),好分清是第几款;label 已经带了同一个序号的
 * 不重复补。模式路径(`products[].name`)与非 repeat 行原样。
 */
export function rowLabel(item: Pick<ChecklistItem, "path" | "label">): string {
  const m = /\[(\d+)\](?!.*\[\d+\])/.exec(item.path);
  if (!m) return item.label;
  const suffix = ` · ${Number(m[1]) + 1}`;
  return item.label.endsWith(suffix) ? item.label : `${item.label}${suffix}`;
}

/**
 * 冲突行要列出的全部候选。api 的形状(`PrefillMerger.fromAi`):`value` / `sourceLabel` / `confidence` 是**排第一的候选**
 * (公开出处优先、置信度高的在前),`conflicts` 只放**其余**候选 —— 只画 `conflicts` 就把最可能对的那个藏了。
 * `value` 为空(旧数据 / 桩里把候选全放进 conflicts 的写法)时只列 conflicts。
 */
export function conflictCandidates(
  item: Pick<ChecklistItem, "value" | "sourceLabel" | "source" | "confidence" | "conflicts">
): ChecklistConflict[] {
  const head: ChecklistConflict[] =
    item.value == null || item.value === ""
      ? []
      : [{ value: item.value, sourceLabel: sourceLabel(item), confidence: item.confidence }];
  return [...head, ...item.conflicts];
}

/** 实例路径 → spec 路径(附录 A.22):`products[0].name` → `products[].name`;`credentials[1]` → `credentials`。 */
export function specPath(path: string): string {
  return path.replace(/\[\d+\]/g, "[]").replace(/\[\]$/, "");
}

/** 一个 spec 字段在清单里的「最好」状态:任一实例已预填 / 已确认 → done;否则任一冲突 → conflict;否则 missing。 */
type SpecBest = "done" | "conflict" | "missing";

const BEST_RANK: Record<SpecBest, number> = { done: 2, conflict: 1, missing: 0 };

/** 不查默认值时用的判据(没有模板 = 不知道哪格有默认)。 */
const NO_DEFAULT = (): boolean => false;

/**
 * 按 spec 字段汇总(附录 A.22 同口径:实例路径归一到 spec 路径,一个字段只数一次,取最好的那个实例状态)。
 * 只数「进计数」的行:{@link isCollectableRow},且不是「缺失但模板有默认值」(那一行画「默认:…」,
 * 同 {@link rowState} 的豁免,A.25)。全 AI 起草 / 默认 / 自动同步的 spec 字段不出现在结果里。
 * ★ 附录 A.39(api `CollectState.doneCount`):有实例、且**每个实例**都是 `excluded`(用户明确答了「一个都不公开」)的字段
 *   算**完成**,不是「不进计数」—— 否则 api 的页头能到 17/17,这里的分组 / 图例却永远少一格、对不上账。
 *   模式路径行(`cases[].client`,没有实例)不算实例,同 api。
 */
function specRollup(
  groups: ReadonlyArray<Pick<ChecklistGroup, "items">>,
  hasDefault: (specPath: string) => boolean
): Map<string, SpecBest> {
  const best = new Map<string, SpecBest>();
  /** spec 路径 → 它的实例行是不是全是 excluded(只记实例行)。 */
  const allExcluded = new Map<string, boolean>();
  for (const g of groups) {
    for (const item of g.items) {
      if (item.type !== "news_feed" && !item.path.includes("[]")) {
        const sp = specPath(item.path);
        allExcluded.set(sp, (allExcluded.get(sp) ?? true) && item.status === "excluded");
      }
      if (!isCollectableRow(item)) continue;
      const sp = specPath(item.path);
      if (item.status === "missing" && hasDefault(sp)) continue;
      const s: SpecBest =
        item.status === "prefilled" || item.status === "confirmed"
          ? "done"
          : item.status === "conflict"
            ? "conflict"
            : "missing";
      const prev = best.get(sp);
      if (prev === undefined || BEST_RANK[s] > BEST_RANK[prev]) best.set(sp, s);
    }
  }
  for (const [sp, excluded] of allExcluded) {
    if (excluded && !best.has(sp)) best.set(sp, "done");
  }
  return best;
}

/**
 * 一组的 done/total,**spec 级**(与页头徽章 `progress` 同口径,附录 A.22 / A.33):三款产品的「产品名」只算一项,
 * 任一款已预填 / 已确认就算完成(冲突与缺失都不算)。分母只算可采集字段 —— excluded / creative / skipped /
 * news_feed 行,以及缺失但模板有默认值的行(画「默认:…」,A.25)既不算完成也不算待办;例外是**全部实例都不公开**
 * 的字段算完成(A.39,同 api 页头分子)。
 * 全组都不可采集(比如「核心优势」全是 AI 起草)时是 0/0,面板不画计数。
 */
/**
 * 1009 C 线:还没做的功能对应的采集分组 —— 「联系表单 + AI 客服」(行全是 `settings.*`:留资表单字段 / AI 客服开关)。
 * 现在发布的官网不带留资表单也没有 AI 客服,这组在清单里只画一行「即将上线」,不让客户以为勾了就有。
 */
export function isComingSoonGroup(group: Pick<ChecklistGroup, "items">): boolean {
  return group.items.length > 0 && group.items.every((i) => i.path.startsWith("settings."));
}

/** 「即将上线」分组那一行。 */
export const COMING_SOON_GROUP_TEXT =
  "官网留资表单 —— 即将上线。官网 AI 客服在「上线」页一键接入,每个页面右下角自动挂浮窗";

export function groupProgress(
  group: Pick<ChecklistGroup, "items">,
  hasDefault: (specPath: string) => boolean = NO_DEFAULT
): SiteProgress {
  const rollup = specRollup([group], hasDefault);
  let done = 0;
  for (const s of rollup.values()) if (s === "done") done += 1;
  return { done, total: rollup.size };
}

export interface LegendCounts {
  done: number;
  conflict: number;
  missing: number;
}

/**
 * 右栏图例「已预填 / 待确认 / 缺失」三格,**spec 级**、只算可采集字段 —— 与页头徽章同一个口径
 * (A.22 / A.33):「已预填」就是徽章的分子;一个字段按它最好的实例状态落进一格,三格加起来是还在计数里的
 * 字段数(全部实例都「不公开」的字段落「已预填」格 —— 用户答完了,A.39,与 api 分子同口径)。带默认值的缺失项
 * 不算缺失(A.25);excluded / skipped / creative / news_feed 行本身都不进。
 */
export function legendCounts(
  groups: ReadonlyArray<Pick<ChecklistGroup, "items">>,
  hasDefault: (specPath: string) => boolean
): LegendCounts {
  const counts: LegendCounts = { done: 0, conflict: 0, missing: 0 };
  for (const s of specRollup(groups, hasDefault).values()) counts[s] += 1;
  return counts;
}

/**
 * 「生成初稿」的闸:必填 + 事实 + 缺失 的项(label 列出来给按钮的 title)。冲突不算缺失 —— 那是有值待选。
 * creative / skipped / news_feed 行不可采集,永远不上榜(它们本来也都不是事实字段;这里再挡一道,
 * 免得 api 在过渡期把 news_feed 记成 missing 时把按钮锁死)。
 */
export function missingRequiredFacts(
  groups: ReadonlyArray<Pick<ChecklistGroup, "items">>
): string[] {
  return groups.flatMap((g) =>
    g.items
      .filter((i) => i.required && i.fact && i.status === "missing" && isCollectableRow(i))
      .map((i) => i.label)
  );
}

/**
 * 「没人碰过」的行状态:缺失;`skipped`(带默认值的非事实字段 / news_feed,新站 api 直接给,附录 A.25 / A.33);
 * `creative`(模板 `source: ai` 的创意字段,附录 A.33)。
 * ★ `excluded` **不算**:它只会在用户答过案例授权题之后出现(A.21),是「有人答过」的证据。
 */
const UNTOUCHED_STATUSES: ReadonlySet<ChecklistStatus> = new Set([
  "missing",
  "skipped",
  "creative",
]);

/**
 * 进页要不要自动跑一次预填:done=0、没有题、每一项都是「没人碰过」的状态(空清单也算)—— 这是
 * 「站刚建好,还没读过知识库」的形状。★ 新站的清单里本来就有 skipped / creative 行,只认 missing
 * 会让自动预填永远不触发;判据错放的代价由页头常驻的「重新预填」兜底。
 */
export function needsPrefill(cl: Checklist): boolean {
  return (
    cl.progress.done === 0 &&
    cl.questions.length === 0 &&
    cl.groups.every((g) => g.items.every((i) => UNTOUCHED_STATUSES.has(i.status)))
  );
}

/** 半角 / 全角括号都认(api 现用半角,见 `FieldState.INTERNAL_SUFFIX`)。 */
const INTERNAL_SUFFIX = /[(\uFF08]内部资料[)\uFF09]\s*$/;

/** 附录 A.26:内部资料命中的项,api 在 `sourceLabel` 末尾加「(内部资料)」;清单据此提示「确认后才公开」。 */
export function isInternalSource(label: string | null | undefined): boolean {
  return typeof label === "string" && INTERNAL_SUFFIX.test(label);
}

/** 默认值本身的人话(不带「默认」前缀):开关 → 开 / 关;列表 → 「姓名 / 手机 / 需求」;枚举 → 选项人话。 */
export function defaultValueLabel(field: Pick<FieldSpec, "default"> | undefined): string | null {
  const v = field?.default;
  if (v === undefined || v === null) return null;
  if (typeof v === "boolean") return v ? "开" : "关";
  if (Array.isArray(v)) {
    const items = v.map((x) => optionLabel(String(x))).filter(Boolean);
    return items.length ? items.join(" / ") : null;
  }
  if (typeof v === "string") return v ? optionLabel(v) : null;
  if (typeof v === "number") return String(v);
  return null;
}

export interface RowState {
  tone: StatusTone;
  text: string;
}

/**
 * 清单一行右侧画什么:ok 画值;冲突「待确认」;excluded「不公开」;缺失但模板有默认值 →「默认:…」
 * (A.25:api 也可能直接给 `skipped`,带值用值、没值用模板默认);其余缺失「缺失」。
 * 附录 A.33:creative →「AI 起草」色调,有值画值、没值画「生成初稿时由 AI 起草」;news_feed 行
 * (api 给 skipped、值为空)一律「自动同步」,不画成「默认」或「缺失」。
 */
/** repeat 组一条都没抽到(清单只剩 pattern 行 `cases[].client`)时右侧那句:资料里没找到,可以直接手填(0925 A12-4)。 */
export const NOT_FOUND_TEXT = "未找到,可手填";

export function rowState(
  item: Pick<ChecklistItem, "status" | "value"> & { type?: FieldType; path?: string },
  field: Pick<FieldSpec, "type" | "default"> | undefined
): RowState {
  if ((item.type ?? field?.type) === "news_feed") {
    return { tone: "default", text: NEWS_SYNC_TEXT };
  }
  switch (item.status) {
    case "creative": {
      const v = displayValue(item.value);
      return { tone: "creative", text: v || CREATIVE_PENDING_TEXT };
    }
    case "excluded":
      return { tone: "excluded", text: STATUS_LABEL.excluded };
    case "conflict":
      return { tone: "warn", text: STATUS_LABEL.conflict };
    case "skipped": {
      // api 的 skipped 行把模板默认值原样放在 value 里(列表 / 开关 / 枚举码),翻人话的规矩同模板默认。
      const v = defaultValueLabel({ default: item.value }) ?? defaultValueLabel(field);
      return { tone: "default", text: v ? `默认:${v}` : STATUS_LABEL.skipped };
    }
    case "missing": {
      const v = defaultValueLabel(field);
      if (v) return { tone: "default", text: `默认:${v}` };
      return {
        tone: "missing",
        text: item.path?.includes("[]") ? NOT_FOUND_TEXT : STATUS_LABEL.missing,
      };
    }
    default:
      return { tone: "ok", text: fieldValueText(item.value, item.type ?? field?.type) };
  }
}

/**
 * 清单里一个值的人话,带上字段类型:枚举字段(价格展示 / 按钮动作…)的码翻成选项人话(`contact` →「询价」,
 * 与屏 2 模板清单同一张表),别的走 {@link displayValue}。未知码原样显示。
 */
export function fieldValueText(value: unknown, type: FieldType | undefined): string {
  if (type === "enum") {
    if (typeof value === "string") return value ? optionLabel(value) : "";
    if (Array.isArray(value)) {
      return value
        .map((v) => (typeof v === "string" ? optionLabel(v) : displayValue(v)))
        .filter(Boolean)
        .join("、");
    }
  }
  return displayValue(value);
}

/**
 * 清单里 `value` 是开放结构(list / cta / toggle / 资产键),给右栏一行字用的人话。
 * 不追求完整,只保证不出现 `[object Object]`。
 */
export function displayValue(value: unknown): string {
  if (value == null || value === "") return "";
  if (typeof value === "string") return value;
  if (typeof value === "number" || typeof value === "boolean") return String(value);
  if (Array.isArray(value)) return value.map(displayValue).filter(Boolean).join("、");
  if (typeof value === "object") {
    const o = value as Record<string, unknown>;
    if (typeof o.label === "string") return o.label;
    if (typeof o.title === "string") return o.title;
    if (typeof o.name === "string") return o.name;
    return JSON.stringify(o);
  }
  return String(value);
}

// ── 追问流 ──────────────────────────────────────────────────────────────────

/** 授权题的题号前缀(ai `questions.py` 出 `q_consent:{repeatKey}`,api `ConsentAnswers.PREFIX`)。 */
export const CONSENT_QUESTION_PREFIX = "q_consent:";

/** 授权题「一个都不勾」时用户气泡里的那句。 */
export const CONSENT_NONE_LABEL = "一个都不公开";

/**
 * 案例授权题:多选、题号以 `q_consent:` 开头。只按题号认 —— 别的多选题(比如次要目标)一个都不勾是没答,
 * 授权题一个都不勾是「都不公开」,两者的发送形状不一样(附录 A.34)。
 */
export function isConsentQuestion(q: Pick<Question, "id" | "kind">): boolean {
  return q.kind === "multi" && q.id.startsWith(CONSENT_QUESTION_PREFIX);
}

/** ai 授权题选项 label 末尾自带的「(✓ 允许公开)」(`questions.py` `_consent_questions`),半角 / 全角括号都认。 */
const CONSENT_LABEL_SUFFIX = /\s*[(\uFF08]\s*✓?\s*允许公开\s*[)\uFF09]\s*$/;

/**
 * 授权题选项的显示名:去掉 ai 自带的「(✓ 允许公开)」—— 那个 ✓ 是写死在字里的,没勾也画着勾,
 * 跟「勾上才打勾」的选中态打架;chip 和用户气泡都只要客户名本身,✓ 由选中态自己画。
 */
export function consentOptionLabel(label: string): string {
  return label.replace(CONSENT_LABEL_SUFFIX, "");
}

/** 选项在题卡 / 气泡里的显示名(授权题去掉后缀,别的题原样)。 */
export function optionDisplayLabel(q: Pick<Question, "id" | "kind">, label: string): string {
  return isConsentQuestion(q) ? consentOptionLabel(label) : label;
}

/** 一道题答出来的东西:发给 api 的答案 + 画成用户气泡的人话。 */
export interface QuestionAnswer {
  answers: CollectAnswer[];
  labels: string[];
}

/**
 * 题 → 答案(纯函数):
 *  - single / text / confirm 写 `fieldPaths[0]`(text 题的 pattern 路径 api 会落到第一项,附录 A.17);
 *  - multi 一个选项一条,第 i 个选项写 `fieldPaths[i]`(案例授权题一家客户一条;没勾的由 api 置 excluded,
 *    附录 A.21,这里**不替它发「未勾选」**);
 *  - 授权题一个都不勾(附录 A.34)→ 恰好一条 `{fieldPath: null, value: [], confirm: true}`,api 把该组全部置
 *    excluded;非授权的多选题一个都不勾不该走到这里(题卡的「确认」是禁用的),真走到了也是空答案、不发;
 *  - confirm 的值是 `true`(「这一项就按现在这样」),api 按 confirm=true 把字段标成 confirmed。
 */
export function toAnswers(question: Question, picked: string[], text: string): QuestionAnswer {
  const base = { questionId: question.id, confirm: true } as const;
  const first = question.fieldPaths[0] ?? null;
  switch (question.kind) {
    case "multi": {
      const chosen = question.options
        .map((o, i) => ({ o, i }))
        .filter(({ o }) => picked.includes(o.value));
      if (chosen.length === 0 && isConsentQuestion(question)) {
        return {
          answers: [{ ...base, fieldPath: null, value: [] }],
          labels: [CONSENT_NONE_LABEL],
        };
      }
      return {
        answers: chosen.map(({ o, i }) => ({
          ...base,
          fieldPath: question.fieldPaths[i] ?? first,
          value: o.value,
        })),
        labels: chosen.map(({ o }) => optionDisplayLabel(question, o.label)),
      };
    }
    case "text": {
      const v = text.trim();
      return { answers: [{ ...base, fieldPath: first, value: v }], labels: [v] };
    }
    case "confirm":
      return { answers: [{ ...base, fieldPath: first, value: true }], labels: ["确认"] };
    default: {
      const o = question.options.find((x) => x.value === picked[0]);
      return {
        answers: [{ ...base, fieldPath: first, value: o?.value ?? picked[0] }],
        labels: [o?.label ?? picked[0] ?? ""],
      };
    }
  }
}

/** ①②③…(超过 20 道退回数字)。 */
export function circled(index: number): string {
  return index < 20 ? String.fromCodePoint(0x2460 + index) : `${index + 1}.`;
}
